import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { message, Card, Col, Typography, Dropdown, Button, Modal, Input, Avatar, List, Tooltip, Empty } from "antd";
import { PlusOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import { getIdeProjectDetails, createFileOrFolder, renameIdeFileOrFolder, deleteFileOrFolder } from "../../services/ide-apis";
import { getIdeProjectTypeMetaData, getIdeProjectLogo, formatDate } from "../utils/util-functions";
import { global_var } from "../../services/cs-lab-config";
import { ReactComponent as OpenIcon } from '../../assets/logos/open project.svg';
import { ReactComponent as OptionIcon } from '../../assets/logos/options.svg';
import { ReactComponent as EditIcon } from '../../assets/logos/edit name.svg';
import noDataImage from '../../assets/images/No notifcations.png';
import IdeHeader from "./ide-header";
import CreateIDEProjectModal from "./create-ide-project-modal";

const { Meta } = Card;

const GoogleAppIde = () => {
  document.title = "CS Lab | Google Apps";

  let { projectStack, ideProjectId } = useParams();

  let hideHeader = new URLSearchParams(window.location.search).get("hideHeader");

  const [ideProjectDetails, setIdeProjectDetails] = useState(undefined);
  const [fileMetaList, setFileMetaList] = useState([]);
  const [newProjectModal, setNewProjectModal] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);

  const [createFileModalVisible, setCreateFileModalVisible] = useState(false);
  const [createFileLoading, setCreateFileLoading] = useState(false);
  const [newFileStack, setNewFileStack] = useState(null);
  const [newFileName, setNewFileName] = useState("");

  const updateProjectDetails = useCallback((updatedProject) => {
    setIdeProjectDetails({ ...updatedProject });
  }, []);

  useEffect(() => {
    if (ideProjectId) {
      setNewProjectModal(false);
      getIdeProjectDetails(ideProjectId).then((response) => {
        response = response.data;

        if (response.statusCode === 200) {
          if (
            response.projectDetails.fkUserLoginId === global_var.user_login_id ||
            global_var.role_id !== global_var.STUDENT_ROLE_ID
          ) {
            const shouldSetReadOnly =
              response.projectDetails.evaluationStatus === "Evaluated" ||
              response.projectDetails.fkUserLoginId !== global_var.user_login_id ||
              response.projectDetails.editDisabled;

            if (shouldSetReadOnly) {
              setReadOnlyMode(true);
            }
            setIdeProjectDetails(response.projectDetails);

            if (response.projectDetails.fileMetaList) {
              setFileMetaList(response.projectDetails.fileMetaList);
            }
          } else {
            message.error("you are not authorised");
          }
        } else {
          message.error(response.message);
        }
      });
    } else {
      setIdeProjectDetails(undefined);
      setNewProjectModal(true);
    }
  }, [ideProjectId]);

  const handleFileClick = (file) => {
    if (file.file_url) {

      const url = new URL(file.file_url);

      url.searchParams.set("hl", "en");

      window.open(url.toString(), "_blank");
      return;
    }
  };

  const handleMenuClick = (e) => {
    const selectedStack = e.key;
    const projectMetaData = getIdeProjectTypeMetaData(selectedStack);

    setNewFileStack(selectedStack);
    setNewFileName(projectMetaData.defaultFileName);
    setCreateFileModalVisible(true);
  };

  const handleRenameFile = (file) => {
    let newFileName = file.fileName;
    Modal.info({
      title: "Rename file",
      content: (
        <Input
          defaultValue={file.fileName}
          placeholder="Enter new file name"
          onChange={(e) => (newFileName = e.target.value)}
          maxLength={40}
        />
      ),
      onOk() {
        if (newFileName.trim() === "") {
          message.error("Please enter a valid file name");
          return;
        }
        if (newFileName === file.fileName) return;

        renameIdeFileOrFolder(ideProjectId, file.id, newFileName, file.isFile).then((response) => {
          response = response.data;
          if (response.statusCode === 200) {
            message.success(response.message);
            setFileMetaList((prevMetaList) =>
              prevMetaList.map((f) => (f.id === file.id ? { ...f, fileName: newFileName } : f))
            );
          } else {
            message.error(response.message);
          }
        });
      }
    });
  };

  const handleDeleteFile = (file) => {
    Modal.confirm({
      title: "Delete file",
      content: "Are you sure you want to delete " + file.fileName + "?",
      okType: 'danger',
      onOk() {
        deleteFileOrFolder(ideProjectId, file.id).then((response) => {
          response = response.data;
          if (response.statusCode === 200) {
            message.success(response.message);
            setFileMetaList((prevMetaList) =>
              prevMetaList.filter((f) => f.id !== file.id)
            );
          } else {
            message.error(response.message);
          }
        });
      }
    });
  };

  const handleCreateFileConfirm = () => {
    if (!newFileName.trim()) {
      message.error("Please enter a valid file name.");
      return;
    }

    setCreateFileLoading(true);

    const projectMetaData = getIdeProjectTypeMetaData(newFileStack);

    const driveFolderId = ideProjectDetails.googleDriveFolderObj ? ideProjectDetails.googleDriveFolderObj.folderId : undefined;

    createFileOrFolder(ideProjectId, {
      fileOrFolderObject: {
        fileName: newFileName,
        isFile: true,
        path: "",
        fileMimeType: projectMetaData.fileMimeType || undefined,
        driveFolderId
      },
    }).then((response) => {
      response = response.data;
      if (response.statusCode === 200) {
        message.success(response.message);
        setFileMetaList((prevList) => [...prevList, response.fileMetaListObj]);
        setCreateFileLoading(false);
        setCreateFileModalVisible(false);
      } else {
        setCreateFileLoading(false);
        message.error(response.message);
      }
    }).catch((err) => {
      setCreateFileLoading(false);
      message.error("Failed to create file.");
    });
  };

  const menuProps = {
    items: [
      { key: 'gdocs', label: 'Google Doc' },
      { key: 'gsheets', label: 'Google Sheet' },
      { key: 'gslides', label: 'Google Slide' },
      { key: 'gforms', label: 'Google Form' },
    ],
    onClick: handleMenuClick,
  };

  // Flatten the fileMetaList for simple rendering (the user specified "only 1 level will be there")
  const renderFiles = () => {
    const getFileLogo = (mimeType) => {
      switch (mimeType) {
        case "application/vnd.google-apps.document":
          return getIdeProjectLogo("gdocs");
        case "application/vnd.google-apps.spreadsheet":
          return getIdeProjectLogo("gsheets");
        case "application/vnd.google-apps.presentation":
          return getIdeProjectLogo("gslides");
        case "application/vnd.google-apps.form":
          return getIdeProjectLogo("gforms");
        default:
          return getIdeProjectLogo(projectStack);
      }
    };

    const filteredFiles = fileMetaList ? fileMetaList.filter((file) => file.isFile) : [];

    return (
      <List
        locale={{
          emptyText: (
            <Empty
              image={noDataImage}
              description={
                <div>
                  <p className="poppins-regular" style={{ fontSize: '18px' }}>No files available in this project.</p>
                </div>
              }
            />
          ),
        }}
        grid={{
          gutter: 10,
          xs: 1,
          sm: 2,
          md: 3,
          lg: 3,
          xl: 3,
          xxl: 3,
        }}
        dataSource={filteredFiles}
        renderItem={(fileObj) => {
          const actions = [
            <Tooltip title={'Open File'}>
              <OpenIcon key="open" onClick={() => handleFileClick(fileObj)} />
            </Tooltip>
          ];

          if (!readOnlyMode) {
            actions.push(
              <Dropdown
                menu={{
                  items: [
                    { label: "Rename file", key: "1", icon: <EditOutlined />, onClick: () => handleRenameFile(fileObj) },
                    { label: "Delete file", key: "2", icon: <CloseOutlined />, danger: true, onClick: () => handleDeleteFile(fileObj) }
                  ]
                }}
                trigger={["click"]}
              >
                <OptionIcon key="options" />
              </Dropdown>
            );
          }

          return (
            <List.Item className="mr-2 mb-3">
              <Card className="glass card-divider" actions={actions}>
                <Meta
                  avatar={
                    <Avatar
                      size={{ xs: 24, sm: 32, md: 40, lg: 34, xl: 50, xxl: 30 }}
                      src={getFileLogo(fileObj.fileMimeType)}
                      style={{ objectFit: "contain"}}
                    />
                  }
                  title={
                    <Typography.Text ellipsis={{ tooltip: fileObj.fileName }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '100%' }}>
                        <span className="poppins-regular" style={{
                          display: 'inline-block',
                          maxWidth: 'calc(100%) - 30px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {fileObj.fileName}
                        </span>
                        {!readOnlyMode && (
                          <Tooltip title={'Edit File Name'}>
                            <EditIcon
                              style={{ marginLeft: '10px', cursor: 'pointer', flexShrink: 0 }}
                              onClick={() => handleRenameFile(fileObj)}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </Typography.Text>
                  }
                  description={
                    <div className="d-flex align-items-center">
                      <Typography.Text className="poppins-regular" style={{ fontSize: '12px' }}>
                        {fileObj.createdDate ? formatDate(fileObj.createdDate) : "Newly created"}
                      </Typography.Text>
                    </div>
                  }
                />
              </Card>
            </List.Item>
          );
        }}
      />
    );
  };

  return (
    <div className="container-fluid p-0">
      <div className="row mt-1">
        <div className="col">
          {ideProjectDetails && hideHeader !== "true" && (
            <IdeHeader
              headerName="Google Workspace"
              showInPortFolio={ideProjectDetails.showInPortFolio}
              submittedForEvaluation={ideProjectDetails.submittedForEvaluation}
              ideProjectDetails={ideProjectDetails}
              fkResourceId={ideProjectDetails.fkResourceId}
              unsavedChangePresent={false}
              readOnlyMode={readOnlyMode}
              onUpdateProjectDetails={updateProjectDetails}
            />
          )}

          <div className="row mt-4 pt-2" style={{ padding: "20px" }}>
            {ideProjectDetails && (
              <Col span={24}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "20px", marginLeft: "15px" }}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Project Files
                  </Typography.Title>
                  {!readOnlyMode && (
                    <Dropdown menu={menuProps} placement="bottomRight">
                      <Button type="primary" icon={<PlusOutlined />} style={{ marginLeft: "auto", marginRight: "15px" }}>
                        Create new file
                      </Button>
                    </Dropdown>
                  )}
                </div>
                <div>
                  {renderFiles()}
                </div>
              </Col>
            )}
          </div>
        </div>

        {newProjectModal && <CreateIDEProjectModal projectStack={projectStack} />}

        <Modal
          title={newFileStack ? `Create ${getIdeProjectTypeMetaData(newFileStack)?.projectStackDisplayName}` : "Create File"}
          open={createFileModalVisible}
          onOk={handleCreateFileConfirm}
          confirmLoading={createFileLoading}
          onCancel={() => setCreateFileModalVisible(false)}
          okText="Create"
          cancelText="Cancel"
          centered
        >
          <Input
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            placeholder="Enter file name"
            autoFocus
            onPressEnter={handleCreateFileConfirm}
          />
        </Modal>
      </div>
    </div>
  );
};

export default GoogleAppIde;
